import type { PrismNoteOutcome } from "./memory"
import type { OnboardingProgress, OnboardingStep } from "@/types/user-settings"

import { BACKEND_URI } from "@/constants/config"
import { prismMemorySnapshot } from "@/stores/memory-backend-store"

import { fetchWithAuthRetry, getHeaders } from "./api"
import { fetchPrismAppRequest } from "./prism-app-request"

export type ContinueChat = {
  chat_id: string
  title: string
  session_type: string
  message_count: number
  updated_iso: string
  score: number
}

export type Theme = {
  label: string
  evidence_count: number
}

export type ActiveProject = {
  id: string
  name: string
  updated_iso: string
}

export type FrequentSource = {
  sourcefile: string
  session_count: number
}

export type GraphFile = {
  id: string
  name: string | null
  weburl: string | null
  last_used_iso?: string
  type?: string
}

export type GraphPerson = {
  id: string
  name: string | null
  email: string | null
  job_title: string | null
  person_type?: {
    class?: string | null
    subclass?: string | null
  } | null
}

export type GraphMeeting = {
  id: string
  subject: string | null
  start_iso: string | null
  end_iso: string | null
  attendees: { name: string | null; email: string | null }[]
  preview?: string | null
}

export type GraphUnreadMailMessage = {
  id: string
  subject: string | null
  sender: string | null
  received_iso: string | null
  weburl: string | null
  importance?: string | null
  has_attachments?: boolean
}

export type GraphUnreadMail = {
  count: number
  messages: GraphUnreadMailMessage[]
  // Top 1–2 most-pressing messages, ranked by importance flag → top-collaborator
  // sender → recency. Surfaced in the Day Brief "Inkorg" section as quick-reply
  // rows. Empty when no mail or no signals are pressing enough.
  highlights?: GraphUnreadMailMessage[]
}

export type WaitingThread = {
  id: string | null
  subject: string | null
  sender: string | null
  received_iso: string | null
  weburl: string | null
}

export type ProjectPulse = {
  id: string
  name: string
  updated_iso: string | null
}

export type HeroState = "default" | "pre_meeting" | "weekly_wrap" | "weekly_plan"

// Per-signal Graph failure flags from the backend's `_graph_error_flags` (see
// routes/home_context.py). A key is present (and true) only when that leg's
// Graph fetch genuinely failed (429/401/timeout/...) — a successful-but-empty
// result never sets a flag. Keys are omitted (not `false`) when that leg
// succeeded, so callers should check truthiness, not presence.
export type GraphErrorFlags = Partial<
  Record<"calendar" | "recent_files" | "unread_mail" | "trending" | "people", boolean>
>

export type UserProfile = {
  name?: string | null
  email?: string | null
  job_title?: string | null
  department?: string | null
  office_location?: string | null
  company?: string | null
  manager_name?: string | null
  manager_email?: string | null
  fetched_at?: string | null
}

export type HomeContextResponse = {
  graph: {
    next_meeting: GraphMeeting | null
    today_meetings?: GraphMeeting[]
    upcoming_meetings: GraphMeeting[]
    // Populated only after 14:00 local time (per-user timezone). Empty list
    // earlier in the day so the "Imorgon" section degrades cleanly.
    tomorrow_meetings?: GraphMeeting[]
    unread_mail?: GraphUnreadMail
    recent_files: GraphFile[]
    trending: GraphFile[]
    people: GraphPerson[]
    timezone: string | null
    // Present when one or more Graph legs failed (as opposed to succeeding with
    // no data) — see `GraphErrorFlags`. Absent/undefined on older payloads.
    errors?: GraphErrorFlags
  }
  helloai: {
    continue_chats: ContinueChat[]
    current_themes: Theme[]
    active_projects: ActiveProject[]
    pending_memory_candidates: number
    frequent_sources: FrequentSource[]
    about_me_not_applied?: boolean | null
    user_instructions: string | null
    onboarding_completed_at: string | null
    user_profile: UserProfile
    // Most-stalled active project (oldest updated_iso). null when there are
    // no active projects.
    project_pulse?: ProjectPulse | null
    // Threads from top collaborators that look like the ball is in your court.
    // Capped at 2; empty when no collaborator-sender unread mail.
    waiting_on_you?: WaitingThread[]
  }
  rendered_starters: string[]
  hero_state: HeroState
}

export type AcceptedMemoryItem = {
  text: string
  type: string
}

export type WipeResult = {
  ok: boolean
  prism?: { total: number; confirmed: number; queued: number }
  // `complete` is false when any memory delete failed or an enumeration errored;
  // `ok` stays true as long as the endpoint itself ran. Callers should check
  // `complete` (and `failed_count`) to distinguish full success from partial.
  complete: boolean
  failed_count: number
  wiped: {
    user_instructions: boolean
    activity_digest: boolean
    memory_items: number
    deep_memory?: boolean
    saved_prompts: boolean
    favorite_documents: boolean
    favorite_sessions: boolean
    dashboard_preferences: boolean
    trusted_sources: boolean
    profile_cache: boolean
  }
}

export type ExportPersonalizationResult = {
  blob: Blob
  filename: string | null
  complete: boolean
  incomplete_categories: string[]
}

function getClientTimezone(): string | null {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || null
  } catch {
    return null
  }
}

export async function getHomeContextApi(idToken?: string): Promise<HomeContextResponse> {
  // Pass the browser's IANA timezone — backend uses it for weekly_wrap/weekly_plan
  // hero gating which would otherwise always evaluate in UTC.
  const tz = getClientTimezone()
  const headers = getHeaders(idToken)
  const headersWithTz = tz ? { ...headers, "X-Client-Timezone": tz } : headers
  const response = await fetchWithAuthRetry(
    `${BACKEND_URI}/personalization/home_context`,
    {
      method: "GET",
      headers: headersWithTz,
    },
    idToken
  )
  return await response.json()
}

export async function refreshGraphApi(idToken?: string): Promise<{ ok: boolean }> {
  const response = await fetchWithAuthRetry(
    `${BACKEND_URI}/personalization/refresh_graph`,
    {
      method: "POST",
      headers: getHeaders(idToken),
    },
    idToken
  )
  return await response.json()
}

export async function saveAboutMeApi(
  text: string,
  idToken?: string,
  options?: { clear?: boolean }
): Promise<{ ok: boolean; user_instructions: string } | PrismNoteOutcome> {
  // In Prism mode the server refuses a blank About me without `clear: true`:
  // only a field the user emptied may forget the live About me event.
  const body = options?.clear
    ? { user_instructions: text, clear: true }
    : { user_instructions: text }
  const response = await (prismMemorySnapshot() ? fetchPrismAppRequest : fetchWithAuthRetry)(
    `${BACKEND_URI}/personalization/about_me`,
    {
      method: "POST",
      headers: getHeaders(idToken),
      body: JSON.stringify(body),
    },
    idToken
  )
  return await response.json()
}

export async function completeOnboardingApi(
  payload: { user_instructions?: string; accepted_memories?: AcceptedMemoryItem[] },
  idToken?: string
): Promise<{
  ok: boolean
  completed_at: string | null
  whats_new_last_seen_version: string | null
}> {
  const response = await (prismMemorySnapshot() ? fetchPrismAppRequest : fetchWithAuthRetry)(
    `${BACKEND_URI}/personalization/onboarding_complete`,
    {
      method: "POST",
      headers: getHeaders(idToken),
      body: JSON.stringify(payload),
    },
    idToken
  )
  const result = (await response.json()) as {
    ok?: boolean
    completed_at?: string | null
    whats_new_last_seen_version?: string | null
    error?: string
  }
  if (!response.ok || result.ok !== true) {
    throw new Error(result.error || "Failed to complete onboarding")
  }
  return {
    ok: true,
    completed_at: result.completed_at ?? null,
    whats_new_last_seen_version: result.whats_new_last_seen_version ?? null,
  }
}

export async function updateOnboardingProgressApi(
  currentStep: OnboardingStep,
  returnTo: string,
  idToken?: string
): Promise<{ ok: boolean; progress: OnboardingProgress }> {
  const response = await fetchWithAuthRetry(
    `${BACKEND_URI}/personalization/onboarding_progress`,
    {
      method: "PUT",
      headers: getHeaders(idToken),
      body: JSON.stringify({ version: 2, current_step: currentStep, return_to: returnTo }),
    },
    idToken
  )
  const result = (await response.json()) as {
    ok?: boolean
    progress?: OnboardingProgress
    error?: string
  }
  if (!response.ok || !result.progress) {
    throw new Error(result.error || "Failed to save onboarding progress")
  }
  return { ok: result.ok === true, progress: result.progress }
}

export async function acceptOnboardingComplianceApi(idToken?: string): Promise<{
  ok: boolean
  ui: { gdpr_accepted_version: string; gdpr_accepted_timestamp: string }
}> {
  const response = await fetchWithAuthRetry(
    `${BACKEND_URI}/personalization/onboarding_compliance`,
    {
      method: "POST",
      headers: getHeaders(idToken),
      body: JSON.stringify({}),
    },
    idToken
  )
  const result = (await response.json()) as {
    ok?: boolean
    ui?: { gdpr_accepted_version: string; gdpr_accepted_timestamp: string }
    error?: string
  }
  if (!response.ok || !result.ui) {
    throw new Error(result.error || "Failed to accept compliance")
  }
  return { ok: result.ok === true, ui: result.ui }
}

export async function forceResetOnboardingApi(idToken?: string): Promise<{ ok: boolean }> {
  const response = await fetchWithAuthRetry(
    `${BACKEND_URI}/personalization/onboarding_force_reset`,
    {
      method: "POST",
      headers: getHeaders(idToken),
      body: JSON.stringify({}),
    },
    idToken
  )
  const result = (await response.json()) as { ok?: boolean; error?: string }
  if (!response.ok) {
    throw new Error(result.error || "Failed to reset onboarding")
  }
  return { ok: result.ok === true }
}

export async function wipePersonalizationApi(idToken?: string): Promise<WipeResult> {
  const response = await fetchWithAuthRetry(
    `${BACKEND_URI}/personalization/wipe`,
    {
      method: "POST",
      headers: getHeaders(idToken),
      body: JSON.stringify({ confirm: true }),
    },
    idToken
  )
  return await response.json()
}

export async function exportPersonalizationApi(
  idToken?: string
): Promise<ExportPersonalizationResult> {
  const response = await fetchWithAuthRetry(
    `${BACKEND_URI}/personalization/export`,
    {
      method: "GET",
      headers: getHeaders(idToken),
    },
    idToken
  )
  // Parse JSON so we can surface `complete` / `incomplete_categories` (same
  // signal wipe already uses). Still hand the caller a Blob for download —
  // re-serializing preserves a pretty-printed file without a second round trip.
  const payload = (await response.json()) as {
    complete?: boolean
    incomplete_categories?: string[]
  }
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" })
  const disposition = response.headers.get("Content-Disposition")
  const filenameMatch = disposition?.match(/filename="?([^";]+)"?/i)
  return {
    blob,
    filename: filenameMatch?.[1] ?? null,
    complete: payload.complete !== false,
    incomplete_categories: Array.isArray(payload.incomplete_categories)
      ? payload.incomplete_categories
      : [],
  }
}
